import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { Tooltip } from "../tooltip"
import { useNavigationContext } from "./navigation-context"
import { NavigationTooltip } from "./navigation-tooltip"
import type { NavigationBadgeType } from "./navigation-badge"

// =============================================================================
// Props
// =============================================================================

export type NavigationLinkProps = {
  label: string
  isActive?: boolean
  /** Tooltip description. Falls back to `label` when omitted. */
  tooltip?: string
  /**
   * When set, the tooltip description renders with a paid/beta callout
   * beneath it.
   */
  badge?: NavigationBadgeType
  render?: useRender.RenderProp
  /** Link content — the icon or dot visual, followed by the label */
  children: React.ReactNode
}

// =============================================================================
// Styles
// =============================================================================

const baseLinkStyles = [
  "isolate group grid grid-cols-[auto_1fr] items-center gap-x-2",
  "w-full h-10 rounded-[10px] p-2",
  "text-sm text-gray-700 select-none",
  "hover:bg-gray-900/[0.06]",
  "outline-2 outline-transparent -outline-offset-2 focus-visible:outline-gray-900",
  "transition-colors",
  "data-active:bg-gray-900/[0.06] data-active:text-gray-900"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * Shared link primitive used by NavigationItem and NavigationGroupItem.
 * Handles the render prop pattern, active/current state, and tooltip.
 * Not part of the public API — internal only.
 */
export function NavigationLink({
  label,
  isActive = false,
  tooltip,
  badge,
  render,
  children
}: NavigationLinkProps) {
  const { collapsed } = useNavigationContext()

  const element = useRender({
    defaultTagName: "a",
    render,
    props: {
      "data-active": isActive ? "" : undefined,
      "aria-current": isActive ? ("page" as const) : undefined,
      className: baseLinkStyles
    }
  })

  const tooltipContent =
    badge && tooltip ? (
      <NavigationTooltip type={badge}>{tooltip}</NavigationTooltip>
    ) : (
      (tooltip ?? label)
    )

  return (
    <Tooltip.Provider delay={0}>
      <Tooltip disabled={isActive || (!collapsed && !tooltip)}>
        <Tooltip.Trigger render={element}>{children}</Tooltip.Trigger>
        <Tooltip.Content position="right" sideOffset={12} maxWidth={192}>
          {tooltipContent}
        </Tooltip.Content>
      </Tooltip>
    </Tooltip.Provider>
  )
}
